import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaEnvelope,
  FaReply,
  FaCheckCircle,
  FaClock,
  FaEye,
  FaTicketAlt,
  FaArrowLeft,
  FaCheck,
  FaTimes,
  FaShieldAlt,
  FaRocket,
  FaUser,
  FaCalendarAlt,
} from "react-icons/fa";
import { HiOutlineSparkles } from "react-icons/hi";
import axios from "axios";
import toast from "react-hot-toast";
import Loader from "../components/common/Loader";
import { useFooter } from "../context/FooterContext";

const MyTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { setShowFooter } = useFooter();

  // Hide footer when component mounts, show when unmounts
  useEffect(() => {
    setShowFooter(false);
    return () => setShowFooter(true);
  }, [setShowFooter]);

  useEffect(() => {
    checkDarkMode();
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const checkMobile = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);

    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  useEffect(() => {
    fetchMyTickets();
  }, []);

  const fetchMyTickets = async () => {
    try {
      const token = localStorage.getItem("token");

      const response = await axios.get(
        "/api/contact/my-tickets",
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error("Fetch tickets error:", error);
      toast.error("Failed to fetch your messages");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkResolved = async (ticketId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `/api/contact/${ticketId}`,
        { status: "resolved" },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Ticket marked as resolved");
      fetchMyTickets();
      setSelectedTicket(null);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const getStatusBadge = (status) => {
    const statuses = {
      pending: {
        label: "Pending",
        color: "#f59e0b",
        icon: <FaClock />,
        bg: "rgba(245,158,11,0.15)",
      },
      read: {
        label: "Read",
        color: "#06b6d4",
        icon: <FaEye />,
        bg: "rgba(6,182,212,0.15)",
      },
      replied: {
        label: "Replied",
        color: "#10b981",
        icon: <FaReply />,
        bg: "rgba(16,185,129,0.15)",
      },
      resolved: {
        label: "Resolved",
        color: "#6c757d",
        icon: <FaCheckCircle />,
        bg: "rgba(108,117,125,0.15)",
      },
    };
    const s = statuses[status] || statuses.pending;
    return (
      <span
        style={{
          background: s.bg,
          color: s.color,
          padding: "0.25rem 0.8rem",
          borderRadius: "20px",
          fontSize: "0.7rem",
          display: "inline-flex",
          alignItems: "center",
          gap: "0.3rem",
          border: `1px solid ${s.color}30`,
          fontWeight: "500",
        }}
      >
        {s.icon} {s.label}
      </span>
    );
  };

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  if (loading) return <Loader />;

  return (
    <div
      style={{
        maxWidth: "1000px",
        margin: "0 auto",
        padding: isMobile ? "1rem" : "2rem",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        minHeight: "calc(100vh - 200px)",
        position: "relative",
      }}
    >
      {/* Animated Background Grid */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage: `radial-gradient(circle at 1px 1px, ${isDarkMode ? "rgba(139,92,246,0.1)" : "rgba(139,92,246,0.08)"} 1px, transparent 1px)`,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Floating Gradient Orbs */}
      <motion.div
        animate={{ x: [0, 100, 0], y: [0, 50, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        style={{
          position: "fixed",
          top: "10%",
          left: "-5%",
          width: "250px",
          height: "250px",
          background:
            "radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)",
          borderRadius: "50%",
          filter: "blur(60px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      <div style={{ position: "relative", zIndex: 1 }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, type: "spring", stiffness: 300 }}
          style={{
            background: "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
            borderRadius: "28px",
            padding: "2rem",
            marginBottom: "2rem",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.1, 0.3] }}
            transition={{ duration: 4, repeat: Infinity }}
            style={{
              position: "absolute",
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              background:
                "radial-gradient(circle, rgba(139,92,246,0.4) 0%, transparent 70%)",
              borderRadius: "50%",
            }}
          />
          <div style={{ position: "relative", zIndex: 1 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                marginBottom: "0.5rem",
              }}
            >
              <div
                style={{
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  width: "50px",
                  height: "50px",
                  borderRadius: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaTicketAlt size={24} color="white" />
              </div>
              <h1
                style={{
                  fontSize: isMobile ? "1.8rem" : "2rem",
                  fontWeight: "bold",
                  color: "white",
                }}
              >
                My <span style={{ color: "#8b5cf6" }}>Support Tickets</span>
              </h1>
            </div>
            <p style={{ color: "rgba(255,255,255,0.9)", marginLeft: "3rem" }}>
              View all your support requests and responses from our team.
            </p>
          </div>
        </motion.div>

        {/* Content */}
        {tickets.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{
              textAlign: "center",
              padding: "4rem",
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              borderRadius: "24px",
              border: `1px solid ${getBorderColor()}`,
            }}
          >
            <div
              style={{
                background: isDarkMode
                  ? "rgba(139,92,246,0.1)"
                  : "rgba(139,92,246,0.05)",
                width: "80px",
                height: "80px",
                borderRadius: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 1rem auto",
              }}
            >
              <FaEnvelope size={40} color="#8b5cf6" />
            </div>
            <h3 style={{ color: getTextColor(), marginBottom: "0.5rem" }}>
              No Tickets Yet
            </h3>
            <p style={{ color: getTextSecondary(), marginBottom: "1.5rem" }}>
              You haven't submitted any support requests yet.
            </p>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Link
                to="/contact"
                style={{
                  display: "inline-block",
                  padding: "0.8rem 2rem",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  color: "white",
                  textDecoration: "none",
                  borderRadius: "12px",
                  fontWeight: "600",
                  boxShadow: "0 4px 14px 0 rgba(139,92,246,0.4)",
                }}
              >
                Contact Support
              </Link>
            </motion.div>
          </motion.div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            style={{ display: "grid", gap: "1rem" }}
          >
            {tickets.map((ticket) => (
              <motion.div
                key={ticket._id}
                variants={itemVariants}
                style={{
                  background: getCardBackground(),
                  backdropFilter: "blur(20px)",
                  borderRadius: "20px",
                  overflow: "hidden",
                  cursor: "pointer",
                  border: `1px solid ${getBorderColor()}`,
                  borderLeft: `4px solid ${
                    ticket.status === "replied"
                      ? "#10b981"
                      : ticket.status === "resolved"
                        ? "#6c757d"
                        : ticket.status === "read"
                          ? "#06b6d4"
                          : "#f59e0b"
                  }`,
                  transition: "all 0.3s",
                }}
                onClick={() =>
                  setSelectedTicket(
                    selectedTicket?._id === ticket._id ? null : ticket,
                  )
                }
              >
                <div style={{ padding: "1.2rem" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "0.5rem",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.8rem",
                          flexWrap: "wrap",
                          marginBottom: "0.5rem",
                        }}
                      >
                        <h3
                          style={{
                            margin: 0,
                            fontSize: "1rem",
                            fontWeight: "bold",
                            color: getTextColor(),
                          }}
                        >
                          {ticket.subject}
                        </h3>
                        {getStatusBadge(ticket.status)}
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "1rem",
                          flexWrap: "wrap",
                        }}
                      >
                        <p
                          style={{
                            color: getTextSecondary(),
                            fontSize: "0.7rem",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.3rem",
                          }}
                        >
                          <FaCalendarAlt size={10} />{" "}
                          {new Date(ticket.createdAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div style={{ color: "#8b5cf6", fontSize: "0.8rem" }}>
                      {selectedTicket?._id === ticket._id ? "▲" : "▼"}
                    </div>
                  </div>
                </div>

                <AnimatePresence>
                  {selectedTicket?._id === ticket._id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      style={{
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          padding: "0 1.2rem 1.2rem 1.2rem",
                          borderTop: `1px solid ${getBorderColor()}`,
                        }}
                      >
                        <div style={{ marginTop: "1rem" }}>
                          <strong
                            style={{
                              color: getTextColor(),
                              fontSize: "0.85rem",
                            }}
                          >
                            Your Message:
                          </strong>
                          <div
                            style={{
                              background: isDarkMode
                                ? "rgba(139,92,246,0.05)"
                                : "#f8f9fa",
                              padding: "1rem",
                              borderRadius: "12px",
                              marginTop: "0.5rem",
                              color: getTextSecondary(),
                              fontSize: "0.85rem",
                              lineHeight: "1.5",
                            }}
                          >
                            {ticket.message}
                          </div>
                        </div>

                        {ticket.adminNotes && (
                          <div style={{ marginTop: "1rem" }}>
                            <strong
                              style={{ color: "#10b981", fontSize: "0.85rem" }}
                            >
                              Support Team Response:
                            </strong>
                            <div
                              style={{
                                background: isDarkMode
                                  ? "rgba(16,185,129,0.1)"
                                  : "#e8f5e9",
                                padding: "1rem",
                                borderRadius: "12px",
                                marginTop: "0.5rem",
                                color: "#10b981",
                                borderLeft: `3px solid #10b981`,
                                fontSize: "0.85rem",
                                lineHeight: "1.5",
                              }}
                            >
                              {ticket.adminNotes}
                            </div>
                            {ticket.repliedAt && (
                              <p
                                style={{
                                  color: getTextSecondary(),
                                  fontSize: "0.65rem",
                                  marginTop: "0.3rem",
                                }}
                              >
                                Replied on:{" "}
                                {new Date(ticket.repliedAt).toLocaleString()}
                              </p>
                            )}
                          </div>
                        )}

                        {ticket.status === "replied" && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            style={{ marginTop: "1rem", textAlign: "right" }}
                          >
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkResolved(ticket._id);
                              }}
                              style={{
                                padding: "0.5rem 1rem",
                                background:
                                  "linear-gradient(135deg, #6c757d, #5a6268)",
                                color: "white",
                                border: "none",
                                borderRadius: "10px",
                                cursor: "pointer",
                                fontSize: "0.8rem",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "0.5rem",
                                fontWeight: "500",
                              }}
                            >
                              <FaCheck size={12} /> Mark as Resolved
                            </motion.button>
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Security Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          style={{
            marginTop: "2rem",
            padding: "1rem",
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
          }}
        >
          <FaShieldAlt size={12} color="#10b981" />
          <span style={{ color: getTextSecondary(), fontSize: "0.7rem" }}>
            Your support tickets are securely stored and encrypted
          </span>
        </motion.div>
      </div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default MyTickets;

